import { DOMSerializer } from '@tiptap/pm/model';
import { fetchProtectedFile, protectedFileId } from '../../../../../Services/AuthenticatedFiles';
import { useSessionStore } from '../../../../../Stores/Session';
import Image from "@tiptap/extension-image";

export const CustomImageExtension = Image.extend({
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      let current = node;
      let generation = 0;
      let objectUrl: string | undefined;
      const dom = document.createElement('span');
      const render = () => {
        const version = ++generation;
        if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = undefined; }
        dom.replaceChildren();
        const src = current.attrs.src;
        if (!protectedFileId(src)) {
          dom.append(DOMSerializer.fromSchema(this.editor.schema).serializeNode(current));
          return;
        }
        dom.textContent = 'Loading image…';
        fetchProtectedFile(src).then((response) => response.blob()).then((blob) => {
          if (version !== generation) return;
          if (!blob.type.startsWith('image/') || blob.type.includes('svg')) throw new Error('Unsupported preview');
          objectUrl = URL.createObjectURL(blob);
          const image = document.createElement('img'); image.src = objectUrl;
          image.alt = current.attrs.alt || 'Attachment'; image.style.maxWidth = '100%';
          image.setAttribute('data-protected-file', src);
          dom.replaceChildren(image);
        }).catch(() => { if (version === generation) dom.textContent = 'Sign in to view image'; });
      };
      const unsubscribe = useSessionStore.subscribe((state, previous) => {
        if (state.session?.fileAccessToken !== previous.session?.fileAccessToken || state.session?.gleapId !== previous.session?.gleapId) render();
      });
      render();
      return { dom, update: (next) => { if (next.type !== current.type) return false; if (current.sameMarkup(next)) { current = next; return true; } current = next; render(); return true; }, destroy: () => { generation++; unsubscribe(); if (objectUrl) URL.revokeObjectURL(objectUrl); } };
    };
  },
  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const style = element.getAttribute("style") || "";
          return style || null;
        },
      },
      width: {
        default: "auto",
        parseHTML: (element: HTMLElement) => {
          const style = element.getAttribute("style") || "";
          const widthMatch = style.match(/width:\s*(\d+)px/);
          return widthMatch ? widthMatch[1] : null;
        },
      },
      height: {
        default: "auto",
        parseHTML: () => "auto",
      },
      maxWidth: {
        default: "100%",
        parseHTML: (element: HTMLElement) => {
          const style = element.getAttribute("style") || "";
          const maxWidthMatch = style.match(/max-width:\s*(\d+)px/);
          return maxWidthMatch ? maxWidthMatch[1] : null;
        },
      },
    };
  },
  renderHTML({ HTMLAttributes }: any) {
    const attrs = { ...HTMLAttributes };
    let style = attrs.style || "";

    const widthMatch = style.match(/width:\s*(\d+)px/);
    if (widthMatch) {
      attrs.width = widthMatch[1];
    }

    const maxWidthMatch = style.match(/max-width:\s*(\d+)px/);
    if (!maxWidthMatch) {
      style = `max-width: 100%; ${style}`;
    }
    attrs.style = style;
    attrs.maxWidth = maxWidthMatch ? maxWidthMatch[1] : "100%";

    attrs.height = "auto";

    const imgElement = ["img", attrs];

    const spanElement = [
      "span",
      { style: `display: flex; width: 100%; padding: 5px 0px;` },
      imgElement,
    ];

    const titleAsUrl =
      typeof attrs.title === "string" ? attrs.title.trim() : "";
    if (titleAsUrl.startsWith("http")) {
      return [
        "a",
        { href: titleAsUrl, target: "_blank", rel: "noopener noreferrer" },
        spanElement,
      ] as any;
    }

    return spanElement as any;
  },
});

export default CustomImageExtension;
